<template>
  <WizardStep
    v-if="hasSetCustomFieldsPermission"
    @submit="submit"
    @reset="reset"
  >
    <template v-slot:content>
      <b-jumbotron
        v-if="elements.length"
        lead="You can preview and interact with your custom fields here."
      >
        <FormGenerator
          :formProperties="elements"
          v-model="previewModel"
          cols-sm="2"
          cols="1"
        >
        </FormGenerator>
      </b-jumbotron>
      <div v-for="(element, index) in elements" :key="element.sequenceNumber">
        <FormElement
          :class="{ 'invalid-element': $v.elements.$each[index].$anyError }"
          v-bind.sync="element"
          @clear="elements.splice(index, 1)"
          @done="stripNullValues(element)"
        ></FormElement>
        <div
          v-if="$v.elements.$each[index].fieldName.$error"
          class="invalid-feedback"
        >
          A field name is required.
        </div>
        <div
          v-if="$v.elements.$each[index].fieldType.$error"
          class="invalid-feedback"
        >
          A field type is required.
        </div>
      </div>
      <v-btn
        v-if="!showTooltip"
        class="mx-2"
        dark
        small
        @click="addNewField"
        :color="addButtonColour"
      >
        <v-icon dark>mdi-plus</v-icon>
      </v-btn>
      <v-tooltip right v-else>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" class="mx-2" dark small :color="addButtonColour">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>
          You can only add a maximum of {{ maxCustomFields }} custom
          fields</span
        >
      </v-tooltip>
    </template>
  </WizardStep>
</template>

<script>
import WizardStep from "@/components/wizard/WizardStep";
import FormElement from "@/components/form-elements/generators/FormElementConfig";
import FormGenerator from "@/components/form-elements/generators/FormGenerator.vue";
import * as mutations from "@/graphql/mutations";
import * as queries from "@/graphql/queries";
import { API, graphqlOperation, JS } from "aws-amplify";
import { required } from "vuelidate/lib/validators";
import { GET_USER_ATTRIBUTES } from "@/store/auth.module";
import MerchantSettingsPermissionsMixin from "@/components/merchantsettings/MerchantSettingsPermissionsMixin.js";

export default {
  mixins: [MerchantSettingsPermissionsMixin],
  components: { WizardStep, FormElement, FormGenerator },
  props: ["heading"],
  data: () => {
    return {
      elements: [],
      maxCustomFields: 5,
      showTooltip: false,
      previewModel: {}
    };
  },
  validations: {
    elements: {
      $each: {
        fieldName: {
          required,
          isDuplicate(fieldName, currentElement) {
            const elementIndex = this.elements.findIndex(
              e => e.fieldName === fieldName
            );

            var isWrong = true;
            this.elements.forEach((element, index) => {
              if (index !== elementIndex) {
                if (element.fieldName == currentElement.fieldName) {
                  isWrong = false;
                }
              }
            });

            return isWrong;
          }
        },
        fieldType: {
          required
        },
        values: {
          $each: {
            individualValue: {
              required
            }
          }
        }
      }
    }
  },
  methods: {
    addNewField() {
      if (this.canAdd)
        this.elements.push({
          sequenceNumber: this.elements.length,
          fieldName: "",
          label: "",
          required: false,
          placeHolder: null,
          fieldType: null,
          checkedValue: null,
          uncheckedValue: null,
          min: null,
          max: null,
          format: null,
          defaultValue: null,
          step: null,
          type: null,
          options: null,
          rows: null,
          maxRows: null,
          isSet: false
        });
    },
    submit() {
      if (!this.hasSetCustomFieldsPermission) return;
      this.$v.elements.$touch();
      if (this.$v.elements.$anyError) {
        return;
      }
      this.elements.forEach(this.stripNullValues);
      this.saveElements();
    },
    stripNullValues(element) {
      for (var property in element) {
        if (element[property] == null) delete element[property];
      }
    },
    async saveElements() {
      const formElements = this.elements.map(this.formatForUpload);

      var result = await API.graphql(
        graphqlOperation(mutations.addMerchantForm, {
          body: {
            merchantForm: {
              merchantCode: await this.getMerchantId(),
              formElements: [...formElements]
            }
          }
        })
      );
    },
    formatForUpload(element) {
      var formattedElement = { sequenceNumber: element.sequenceNumber };
      delete element.sequenceNumber;
      delete element.isSet;
      formattedElement.sequenceNumber++;
      formattedElement.formElement = JSON.stringify(element);
      return formattedElement;
    },
    async getMerchantId() {
      var result = null;
      await this.$store.dispatch(GET_USER_ATTRIBUTES).then(attributes => {
        result = attributes["family_name"];
      });
      if (!result) throw new Error("No merchantId exists for this user.");
      return result;
    },
    reset() {
      this.elements = [];
    }
  },
  async created() {
    var result = await API.graphql(
      graphqlOperation(queries.getMerchantForm, {
        params: {
          merchant_code: await this.getMerchantId()
        }
      })
    );
    if (result.data.getMerchantForm)
      this.elements = this.elements.concat(
        result.data.getMerchantForm.formElements.map(function(e) {
          var element = JSON.parse(e.formElement);
          element.isSet = true;
          return element;
        })
      );
  },

  computed: {
    addButtonColour() {
      return this.canAdd ? "indigo" : "red";
    },
    canAdd() {
      return this.elements.length < this.maxCustomFields;
    }
  },
  watch: {
    elements: function(value) {
      if (this.elements.length > 4) this.showTooltip = true;
    }
  }
};
</script>

<style>
.invalid-feedback {
  display: block !important;
  font-size: 100% !important;
}
.invalid-element > select {
  border-color: #fd397a !important;
}
</style>
